import { useState } from "react";
import { Alert, Box, Button, Stack, TextField } from "@mui/material";

import { Refusal, kernelPost } from "@lopie/kernel-ui";
import { formatPhone } from "./phone.js";

/**
 * Text box. Calls the kernel's message.send with this app's own token; the
 * kernel queues it and a separate worker holding the Twilio credential sends
 * it. A 202 means accepted, not delivered, and the note says so.
 *
 * A fresh idempotency key per intended send. This box does not retry, so it
 * never needs to reuse one.
 */
export default function Composer({ token, to, fresh, onQueued }) {
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const send = async () => {
    if (!to || !body.trim()) return;
    setBusy(true);
    setResult(null);
    const key = (crypto.randomUUID && crypto.randomUUID()) || `${Date.now()}-${Math.random()}`;
    try {
      const post = kernelPost(token);
      const res = await post("/actions/message.send", { to, body, idempotency_key: key });
      onQueued({ key, to, body, at: new Date().toISOString() });
      setResult({ ok: true, res });
      setBody("");
    } catch (e) {
      setResult({ ok: false, error: e });
    } finally {
      setBusy(false);
    }
  };

  const verb = fresh ? "Text" : "Reply to";

  return (
    <Stack spacing={1}>
      <TextField
        label={to ? `${verb} ${formatPhone(to)}` : verb}
        multiline
        minRows={2}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send();
        }}
        size="small"
        fullWidth
      />
      <Box>
        <Button variant="contained" disabled={busy || !to || !body.trim()} onClick={send}>
          {busy ? "Sending\u2026" : "Send text"}
        </Button>
      </Box>
      {result?.ok ? (
        <Alert severity="info" variant="outlined">
          Accepted, not yet confirmed sent.{" "}
          {(result.res?.does_not_prove || []).join(" ")}
        </Alert>
      ) : null}
      {result && !result.ok ? (
        result.error?.isManifest ? (
          <Refusal failure={result.error} actor="SMS Inbox" />
        ) : (
          <Alert severity="error" variant="outlined">
            Not sent: {result.error?.body?.detail || result.error?.message || "refused"}
          </Alert>
        )
      ) : null}
    </Stack>
  );
}
